import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";
import { findCityBySlug } from "@/data/cities";

/**
 * GeoLanding — página institucional focada em SEO local por cidade.
 *
 * Reusa o layout completo da Home (hero rotativo, cases, clientes, insights,
 * CTA) MAS sobrescreve toda a metatag <head> com valores específicos daquela
 * cidade (título, description, canonical, OG, LocalBusiness JSON-LD com
 * areaServed e geo coordinates). Isso permite RANK LOCAL em Google Maps + SERP
 * de "agência de marketing imobiliário em [Cidade]" sem duplicar código de UI.
 *
 * Duas rotas apontam pra este componente:
 *   /:citySlug                        (curta, ex: /goiania)
 *   /marketing-imobiliario-:citySlug  (keyword-rich, ex: /marketing-imobiliario-goiania)
 * A segunda é a canonical (peso maior no ranking). A curta gera <link rel="canonical">
 * apontando pra ela — evita conteúdo duplicado penalizado pelo Google.
 */
const SITE_URL = "https://agenciaplaces.com.br";

export default function GeoLanding({ variant: variantProp }) {
  const params = useParams();
  // Support 3 route shapes:
  //   /:citySlug                                      → params.citySlug
  //   /marketing-imobiliario/:citySlug                → params.citySlug
  //   /:fullSlug (catch-all: "goiania" or "marketing-imobiliario-goiania")
  // We normalize by stripping the optional "marketing-imobiliario-" prefix.
  const raw = (params.citySlug || params.fullSlug || params.slug || "").toLowerCase();
  const isCanonical = raw.startsWith("marketing-imobiliario-");
  const slug = isCanonical ? raw.replace(/^marketing-imobiliario-/, "") : raw;
  const variant = variantProp || (isCanonical ? "canonical" : "short");
  const city = findCityBySlug(slug);

  // Analytics — logs a single visit per session per (city, variant).
  // Uses sessionStorage as a cheap throttle so reloads don't inflate counters.
  // Placed BEFORE the early return to keep hook order stable across renders.
  useEffect(() => {
    if (typeof window === "undefined" || !city) return;
    const key = `geo_track_${city.slug}_${variant}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const q = new URLSearchParams(window.location.search);
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/geo/track`, {
      city_slug: city.slug,
      variant,
      referrer: document.referrer || "",
      utm_source: q.get("utm_source") || "",
      utm_medium: q.get("utm_medium") || "",
      utm_campaign: q.get("utm_campaign") || "",
    }).catch(() => {}); // fire-and-forget — never blocks the page
  }, [city, variant]);

  // Slug inválido — usa 404 padrão do site (mantém canonical/SEO limpo).
  if (!city) return <NotFound />;


  const canonicalUrl = `${SITE_URL}/marketing-imobiliario-${city.slug}`;
  const shortUrl = `${SITE_URL}/${city.slug}`;
  const inCity = city.country === "PT" ? `em ${city.name}` : `em ${city.name}${city.state ? "/" + city.state : ""}`;
  const title = `Agência de Marketing Imobiliário ${inCity} | Places — 35 anos de mercado`;
  const description = `Places em ${city.name}: agência de marketing imobiliário com 35 anos de mercado, R$15bi+ em VGV lançados. Branding, campanhas, performance digital e placemaking para incorporadoras${city.country === "BR" ? ` em ${city.state_name}` : ` em ${city.state_name}, ${city.country === "PT" ? "Portugal" : "Paraguai"}`}.`;
  const h1 = `Agência de marketing imobiliário ${inCity}`;

  // JSON-LD LocalBusiness — sinaliza pro Google atendimento local com geo.
  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${canonicalUrl}#business`,
    name: `Places — Marketing Imobiliário ${inCity}`,
    image: `${SITE_URL}/og-default.jpg`,
    url: canonicalUrl,
    telephone: "+5562999999999",
    priceRange: "$$$",
    description,
    areaServed: [
      { "@type": city.country === "BR" ? "City" : "AdministrativeArea", name: city.name },
      { "@type": city.country === "BR" ? "State" : "AdministrativeArea", name: city.state_name },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: city.state,
      addressCountry: city.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: city.latitude,
      longitude: city.longitude,
    },
    knowsAbout: [
      "Marketing Imobiliário",
      "Branding Imobiliário",
      `Marketing Imobiliário ${city.name}`,
      `Branding Imobiliário ${city.name}`,
      `Agência ${city.name}`,
    ],
    sameAs: [
      "https://www.instagram.com/placesagencia/",
      "https://www.linkedin.com/company/places-marketing/",
    ],
  };

  // Breadcrumbs JSON-LD
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: city.country === "BR" ? city.state_name : city.country === "PT" ? "Portugal" : "Paraguai", item: `${SITE_URL}/regioes/${city.state.toLowerCase()}` },
      { "@type": "ListItem", position: 3, name: city.name, item: canonicalUrl },
    ],
  };

  return (
    <>
      {/* Banner discreto acima do Home indicando atendimento local — apenas
          uma faixa pequena para o usuário perceber o contexto. Não substitui
          H1 (esse continua sendo o hero rotativo). */}
      <div className="theme-light pt-24 pb-0" data-testid={`geo-badge-${city.slug}`}>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full bg-black text-white">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-yellow" />
            Atendimento local · {city.name}{city.country === "BR" ? ` / ${city.state}` : ` / ${city.country}`}
          </div>
          {/* H1 invisível (only-visible-to-crawlers) — o hero da Home vira o H1 visual,
              mas o Google ainda quer um H1 keyword-rich no primeiro dobra. */}
          <h1 className="sr-only" data-testid={`geo-h1-${city.slug}`}>{h1} — Places</h1>
        </div>
      </div>

      <Home skipSEO />

      {/*
        Este Helmet é montado APÓS o <SEO> interno do <Home />, então tem
        prioridade — react-helmet-async pega o mais recente por tag. Injetamos
        title/description/OG/canonical/JSON-LD específicos da cidade.
      */}
      <Helmet prioritizeSeoTags>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />
        {/* Curta e keyword-rich apontam pra mesma canonical — resolve duplicidade. */}
        {variant === "short" && <link rel="alternate" href={canonicalUrl} />}

        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Places — Marketing Imobiliário" />
        <meta property="og:image" content={`${SITE_URL}/og-default.jpg`} />
        <meta property="og:locale" content={city.country === "PT" ? "pt_PT" : city.country === "PY" ? "es_PY" : "pt_BR"} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-default.jpg`} />

        <meta name="geo.region" content={`${city.country}-${city.state}`} />
        <meta name="geo.placename" content={city.name} />
        <meta name="geo.position" content={`${city.latitude};${city.longitude}`} />
        <meta name="ICBM" content={`${city.latitude}, ${city.longitude}`} />

        <script type="application/ld+json">{JSON.stringify(localBusinessLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>
    </>
  );
}
